import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeroIllustration } from '../components/mte/hero-asset-img';
import { Layout, Header, Card, Button, ProgressBar } from '../components/mte/shell';
import { HERO_CELLS, type HeroCell, type HeroId } from '../components/mte/placeholders';
import { loadProgress } from '../lib/mission-progress';
import { getActiveProfile } from '../lib/profiles';
import styles from './MissionRentree.module.css';

/**
 * MissionRentree - ecran 9/11 (D-NX-072, livre 2026-09-12).
 *
 * Capa / entree de la Mission Rentree. Affiche l'etat courant du chemin
 * 15 etapes + jalons 5/10/15 + CTA primaire "Continuer" (ou "Commencer").
 *
 * Source spec Boussole :
 *  - `mte-product/02-user-flows.md` (B4 - flux Mission Rentree)
 *  - `mte-product/05-dashboard-map.md` (7 - Mission Rentree sur la carte)
 *
 * Route canon : `/enfant/mission/rentree/` (cf. `mte-architecture/04-routing.md` 6.2).
 *
 * Marqueurs canon respectes :
 *  - 6 heros fixes (D-NX-038)
 *  - Sabio mentor UNIVERSEL, NON selectionnable (D-NX-042)
 *  - Mundo unique partage (D-NX-036 / M6)
 *  - Phrase d'entree canon (immuable) : "Réveille tes pouvoirs pour bien
 *    commencer l'annee !" (cf. `05-dashboard-map.md` 7)
 *  - 15 étapes x 6 questions = 90 items (D-NX-015)
 *  - Jalons 5/10/15 = évolution visuelle sobre du heros (D-NX-036 / M3)
 *  - Pas de celebration cinematique en Vague 2 (D-NX-036 / M3b)
 *  - Pas d'ouverture automatique du Parcours Annuel a la fin (D-NX-036 / M4)
 *  - Critere d'avancement = competences + pattern d'erreurs + consolidation,
 *    PAS seulement fin des 15 etapes (D-NX-036 / F9)
 *  - AppBar Sortie parent TOUJOURS visible (D-UF-03)
 *  - Tap targets >= --touch-xl (96x96) sur CTA primaire
 *
 * Persistance Vague 2 (ANA-DIRECT 09:55) :
 *  - currentStep vient de localStorage (loadProgress)
 *  - Default fallback = mock etape 3 si pas de sauvegarde
 *  - Sauvegarde reelle via saveProgress() sur l'ecran MissionEtape
 *
 * Visual rebrand Vague 3 (ANA-DIRECT 13/09 ~22:15) :
 *  - Carte SVG château+chemin extraite du mockup Wave 3
 *    (cf. `mte-design/mockups/w3-mission-rentree.html` L153-166) intégrée
 *    comme bgMap decorativo no hero block (opacity 0.35, pointer-events none)
 *  - Aucune modif aux copy canon ou a la semantique
 *
 * Etats couverts (sous-composant MissionCard) :
 *  - `not_started` : phrase canon + CTA "Commencer l'etape 1"
 *  - `in_progress` : "Etape N sur 15" + ProgressBar + jalon surligne + CTA "Continuer"
 */

type MissionState = 'not_started' | 'in_progress' | 'completed';

const TOTAL_STEPS = 15;
const MOCK_REACHED_MILESTONES: ReadonlyArray<5 | 10 | 15> = [];

const ENTRANCE_PHRASE = 'Réveille tes pouvoirs pour bien commencer l\u2019annee\u00a0!'; // canon immuable

const MILESTONES: ReadonlyArray<5 | 10 | 15> = [5, 10, 15];

const HERO_BY_ID: ReadonlyMap<HeroId, HeroCell> = new Map(
  HERO_CELLS.filter((c) => c.id !== 'sage').map((c) => [c.id, c]),
);

function getHero(id: HeroId | null): HeroCell {
  if (!id) {
    return HERO_CELLS[0] as HeroCell;
  }
  const cell = HERO_BY_ID.get(id);
  if (!cell) {
    return HERO_CELLS[0] as HeroCell;
  }
  return cell;
}

function nextMilestone(step: number): 5 | 10 | 15 | null {
  return MILESTONES.find((m) => m > step) ?? null;
}

export default function MissionRentree(): JSX.Element {
  const active = getActiveProfile();
  const activeId = active?.id;
  const hero = getHero(active?.heroId ?? null);
  const [currentStep, setCurrentStep] = useState<number>(3);
  const [completedSteps, setCompletedSteps] = useState<number[]>([1, 2]);

  // Charger depuis localStorage au mount (Vague 2 - ANA-DIRECT 09:55).
  useEffect(() => {
    const progress = activeId ? loadProgress(activeId) : loadProgress('default');
    setCurrentStep((prev) => (prev === progress.currentStep ? prev : progress.currentStep));
    setCompletedSteps((prev) => {
      if (prev.length === progress.completedSteps.length &&
          prev.every((n, i) => n === progress.completedSteps[i])) {
        return prev;
      }
      return progress.completedSteps;
    });
  }, [activeId]);

  const next = nextMilestone(currentStep);
  const missionState: MissionState =
    completedSteps.length === 0
      ? 'not_started'
      : currentStep > TOTAL_STEPS
        ? 'completed'
        : 'in_progress';

  const appBar = (
    <Header
      context="enfant"
      user={
        <div className={styles.appBar} data-testid="app-bar">
          <div className={styles.appBarIdentity}>
            <HeroIllustration cell={hero} />
            <p className={styles.appBarHello}>
              <span className={styles.appBarHelloAccent}>{active?.firstName ?? "Aventurier"}</span>
              {' . '}
              {hero.label}
            </p>
          </div>
          <Link
            to="/enfant/aventure/"
            className={styles.sortieLink}
            data-action="sortie-parent"
            aria-label="Sortie parent"
          >
            <Button tone="ghost" size="md" context="enfant">
              Sortie parent
            </Button>
          </Link>
        </div>
      }
    />
  );

  return (
    <Layout context="enfant" header={appBar} width="child">
      <div className={styles.eyebrowRow}>
        <p className={styles.eyebrow}>Mission Rentrée</p>
        <p className={styles.screenLabel} aria-label="Écran 9 sur 11">9 / 11</p>
      </div>

      <section className={styles.hero} aria-labelledby="mission-rentree-title">
        {/* Carte SVG château+chemin decorative (Vague 3 - ANA-DIRECT 13/09 ~22:15).
            Extraite verbatim du mockup Wave 3 (`mte-design/mockups/w3-mission-rentree.html`
            L153-166). Pointer-events: none pour ne pas interferir com o CTA. */}
        <svg
          className={styles.bgMap}
          viewBox="0 0 480 300"
          preserveAspectRatio="xMidYMid slice"
          role="presentation"
          aria-hidden="true"
          data-testid="mission-bg-map"
        >
          <path d="M 40 220 Q 120 120 200 200 T 380 140 T 460 180" stroke="#3A6FB0" strokeWidth="3" fill="none" strokeDasharray="2 6" opacity="0.6" />
          <path d="M 0 260 L 60 200 L 100 240 L 150 180 L 200 230 L 260 190 L 320 240 L 380 200 L 440 240 L 480 220 L 480 300 L 0 300 Z" fill="#EFE6D2" />
          <circle cx="60" cy="240" r="14" fill="#3F9D6E" opacity="0.6" />
          <circle cx="80" cy="245" r="10" fill="#3F9D6E" opacity="0.7" />
          <circle cx="320" cy="245" r="12" fill="#3F9D6E" opacity="0.6" />
          <circle cx="340" cy="250" r="9" fill="#3F9D6E" opacity="0.7" />
          <rect x="430" y="150" width="36" height="40" fill="#D9BF8B" stroke="#A07740" strokeWidth="1.5" />
          <path d="M 430 150 L 448 130 L 466 150 Z" fill="#3A6FB0" />
          <rect x="442" y="170" width="10" height="20" fill="#3A2710" />
          <circle cx="40" cy="220" r="12" fill="#D4A93A" />
          <circle cx="40" cy="220" r="20" fill="none" stroke="#D4A93A" strokeWidth="2" opacity="0.5" />
          <circle cx="40" cy="220" r="28" fill="none" stroke="#D4A93A" strokeWidth="1" opacity="0.3" />
        </svg>
        <div className={styles.heroIllustration} aria-hidden="true">
          <HeroIllustration cell={hero} pose="flight_up" />
        </div>
        <div className={styles.heroCopy}>
          <h1 id="mission-rentree-title" className={styles.title}>Mission Rentrée</h1>
          <p className={styles.entrancePhrase}>{ENTRANCE_PHRASE}</p>
        </div>
      </section>

      <MissionCard
        state={missionState}
        currentStep={currentStep}
        nextMilestone={next}
      />

      <div className={styles.actions}>
        <Link to="/enfant/mission/rentree/etape/">
          <Button
            tone="primary"
            size="xl"
            context="enfant"
            block
            data-action={missionState === 'not_started' ? 'commencer-etape-1' : 'continuer-etape'}
          >
            {missionState === 'not_started'
              ? "Commencer l\u2019étape 1"
              : `Continuer l\u2019étape ${currentStep}`}
          </Button>
        </Link>
        <Link to="/enfant/aventure/" data-action="retour-carte">
          <Button tone="ghost" size="md" context="enfant" block>
            Retour à la carte
          </Button>
        </Link>
      </div>

      <Card>
        <div className={styles.milestones}>
          <p className={styles.milestonesEyebrow}>Jalons sur ton chemin</p>
          <ul className={styles.milestonesList} data-testid="milestones">
            {MILESTONES.map((m) => {
              const reached = currentStep >= m || MOCK_REACHED_MILESTONES.includes(m);
              const isNext = next === m;
              return (
                <li
                  key={m}
                  className={styles.milestone}
                  data-milestone={m}
                  data-reached={reached ? 'true' : 'false'}
                  data-next={isNext ? 'true' : 'false'}
                  aria-label={
                    isNext
                      ? `Jalon ${m} : prochain`
                      : reached
                        ? `Jalon ${m} : franchi`
                        : `Jalon ${m} : à venir`
                  }
                >
                  <span className={styles.milestoneDot} aria-hidden="true" />
                  <span className={styles.milestoneNumber}>{m}</span>
                </li>
              );
            })}
          </ul>
          <p className={styles.milestonesNote}>
            Quand tu atteins un jalon, {hero.label} évolue visuellement.
          </p>
        </div>
      </Card>
    </Layout>
  );
}

function MissionCard({
  state,
  currentStep,
  nextMilestone,
}: {
  state: MissionState;
  currentStep: number;
  nextMilestone: 5 | 10 | 15 | null;
}): JSX.Element {
  return (
    <Card>
      <div className={styles.cardHeader}>
        <p className={styles.cardEyebrow}>
          {state === 'not_started'
            ? 'À commencer'
            : state === 'in_progress'
              ? 'En cours'
              : 'Terminée'}
        </p>
        <p className={styles.cardCounter} data-testid="card-counter">
          {state === 'not_started'
            ? `${TOTAL_STEPS} étapes au total`
            : `Étape ${currentStep} sur ${TOTAL_STEPS}`}
        </p>
      </div>

      <ProgressBar current={state === "completed" ? TOTAL_STEPS : currentStep} total={TOTAL_STEPS} milestones={[5, 10, 15]} label="Progression de la Mission Rentrée" />

      <ul className={styles.cardMeta}>
        <li className={styles.cardMetaItem}>
          <span aria-hidden="true">&#x2B50;</span>
          <span>15 étapes x 6 questions = 90 challenges</span>
        </li>
        <li className={styles.cardMetaItem}>
          <span aria-hidden="true">&#x1F989;</span>
          <span>
            {nextMilestone
              ? `Prochain jalon : etape ${nextMilestone}`
              : 'Tous les jalons franchis'}
          </span>
        </li>
        <li className={styles.cardMetaItem}>
          <span aria-hidden="true">&#x1F6E1;&#xFE0F;</span>
          <span>Tu peux t&apos;arreter et revenir quand tu veux</span>
        </li>
      </ul>
    </Card>
  );
}
